'use client'

import { useSelectedLayoutSegment } from 'next/navigation'
import { clsx } from 'clsx'

import { ButtonLink } from '~/sotano-kit/primitives/button'
import { useLantern } from '~/hooks/use-lantern'
import { useUserStore } from '~/app/_store/user-store'
import { encodeNextParam } from '~/app/(mkt)/_index-redirector/server'
import { getLoginHref } from '~/app/(dash)/(login)/login/get-login-href'
import { useHasRendered } from '~/hooks/use-has-rendered'

export type ActionsProps = {
  loginNext?: string
  preLabel?: string
}

export const Actions = ({ loginNext, preLabel }: ActionsProps) => {
  const hasRendered = useHasRendered()
  const optimisticUser = useUserStore((s) => s.optimisticUser)

  const selectedLayoutSegment = useSelectedLayoutSegment()
  const { maskRef, elementRef } = useLantern<HTMLAnchorElement>({
    size: 'sm',
  })

  const signupLink = `/signup${
    loginNext ? `?next=${encodeNextParam(loginNext)}` : ''
  }`
  const loginLink = `/login${
    loginNext ? `?next=${encodeNextParam(loginNext)}` : ''
  }`

  return (
    <>
      {preLabel && (
        <p className="text-xs text-light-muted/80 hidden lg:block">
          {preLabel}
        </p>
      )}

      <div ref={elementRef} className="relative hidden lg:block">
        <div
          className={clsx(
            selectedLayoutSegment === 'login' &&
              'absolute bottom-0 left-1/2 h-px w-2/3 -translate-x-1/2 bg-gradient-highlight'
          )}
        />
        <ButtonLink
          href={hasRendered ? getLoginHref(loginLink) : loginLink}
          intent="secondary"
          size="small"
          pointer
          className="pointer-events-auto"
          suppressHydrationWarning
        >
          {optimisticUser ? 'Dashboard' : 'Log In'}
        </ButtonLink>
        <ButtonLink
          href={hasRendered ? getLoginHref(loginLink) : loginLink}
          className="pointer-events-none absolute top-0 left-0 z-10 !border-dark-control-base !bg-[transparent] opacity-0"
          intent="secondary"
          ref={maskRef}
          tabIndex={-1}
          style={{
            opacity: 'var(--opacity)',
            ['--opacity' as string]: 0,
          }}
          aria-hidden
          size="small"
          pointer
          suppressHydrationWarning
        >
          {optimisticUser ? 'Dashboard' : 'Log In'}
        </ButtonLink>
      </div>

      {!optimisticUser && (
        <ButtonLink
          href={hasRendered ? getLoginHref(signupLink) : signupLink}
          className="hidden md:inline-flex pointer-events-auto -ml-1"
          size="small"
          pointer
        >
          Sign Up
        </ButtonLink>
      )}
    </>
  )
}
